import { Box, Button, Card, Divider, Link, Text, Title } from '@nimbus-ds/components';
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type Corrida, type Programacion } from '../api';
import {
  algunaAcotaPorCategoria,
  descripcionDeRegla,
  useNombresDeCategorias,
} from '../categorias';
import { Cargando, ErrorCaja, EtiquetaEstado } from '../componentes/Estado';
import { Layout } from '../componentes/Layout';
import { ModalDeshacer } from '../componentes/ModalDeshacer';
import { estadoDeshacer } from '../deshacer';
import { aErrorVisible, type ErrorVisible } from '../errores';
import { fechaHoraRelativa, numero } from '../formato';
import { useSesion } from '../sesion';

type Estado =
  | { readonly fase: 'cargando' }
  | { readonly fase: 'listo'; readonly corridas: Corrida[] }
  | { readonly fase: 'error'; readonly error: ErrorVisible };

/** Historial de actualizaciones. Es la pantalla de entrada de la app. */
export function Inicio(): ReactElement {
  const [estado, setEstado] = useState<Estado>({ fase: 'cargando' });
  /**
   * Para la tarjeta de estado, no para la lista.
   *
   * `null` mientras no llegaron **y también si fallaron**: la tarjeta simplemente no
   * muestra esa mitad. El historial no puede quedarse sin pantalla porque una consulta
   * accesoria haya fallado.
   */
  const [programaciones, setProgramaciones] = useState<Programacion[] | null>(null);
  /** La corrida que el comerciante está por deshacer. `null` con el modal cerrado. */
  const [aDeshacer, setADeshacer] = useState<Corrida | null>(null);
  const [deshaciendo, setDeshaciendo] = useState<string | null>(null);
  const [avisoDeshacer, setAvisoDeshacer] = useState<string | null>(null);
  const [error, setError] = useState<ErrorVisible | null>(null);
  const navigate = useNavigate();
  const sesion = useSesion();

  const categorias = useNombresDeCategorias(
    estado.fase === 'listo' && algunaAcotaPorCategoria(estado.corridas),
  );

  const cargar = useCallback(async (): Promise<Corrida[] | null> => {
    try {
      const { corridas } = await api.corridas();
      setEstado({ fase: 'listo', corridas });
      return corridas;
    } catch (excepcion) {
      setEstado({
        fase: 'error',
        error: aErrorVisible(excepcion, 'No pudimos cargar el historial.'),
      });
      return null;
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Va aparte del historial y sin bloquearlo: es un dato de la tarjeta de estado, no de la
  // lista. `/programaciones` sale de nuestra base y no le pega a la API de Tienda Nube, así
  // que no gasta el rate limit compartido del comerciante.
  useEffect(() => {
    let vigente = true;
    api
      .programaciones()
      .then(({ programaciones: lista }) => {
        if (vigente) setProgramaciones(lista);
      })
      .catch(() => undefined);
    return () => {
      vigente = false;
    };
  }, []);

  async function deshacer(corrida: Corrida): Promise<void> {
    setDeshaciendo(corrida.id);
    setError(null);
    setAvisoDeshacer(null);
    try {
      const resultado = await api.deshacer(corrida.id);
      setADeshacer(null);
      if (resultado.aRestaurar > 0) {
        navigate(`/corridas/${resultado.corridaId}`);
        return;
      }
      // No hubo nada que restaurar: no hay corrida nueva que mirar, así que se queda acá
      // con el motivo a la vista.
      setAvisoDeshacer(resultado.aviso ?? 'No había ningún precio para volver atrás.');
      await cargar();
    } catch (excepcion) {
      setADeshacer(null);
      setError(aErrorVisible(excepcion, 'No pudimos deshacer la actualización.'));
    } finally {
      setDeshaciendo(null);
    }
  }

  const descripcion = (corrida: Corrida): string | null =>
    descripcionDeRegla(corrida, categorias);

  return (
    <Layout
      titulo="Actualizaciones de precios"
      acciones={
        <Button
          appearance="primary"
          disabled={sesion !== null && !sesion.acceso.puedeActualizar}
          onClick={() => navigate('/nueva')}
        >
          Nueva actualización
        </Button>
      }
    >
      {estado.fase === 'cargando' && <Cargando />}
      {estado.fase === 'error' && (
        <ErrorCaja mensaje={estado.error.mensaje} codigo={estado.error.codigo} />
      )}
      {error !== null && <ErrorCaja mensaje={error.mensaje} codigo={error.codigo} />}
      {avisoDeshacer !== null && (
        <ErrorCaja titulo="Hay precios que no revertimos" mensaje={avisoDeshacer} />
      )}

      {estado.fase === 'listo' && estado.corridas.length === 0 && (
        <PrimeraVez
          bloqueada={sesion !== null && !sesion.acceso.puedeActualizar}
          dias={sesion?.plan.diasDeRetencionSnapshot ?? null}
          onEmpezar={() => navigate('/nueva')}
        />
      )}

      {estado.fase === 'listo' && estado.corridas.length > 0 && (
        <>
          <EstadoDeLaTienda
            ultima={ultimaQueCorrio(estado.corridas)}
            descripcion={descripcion}
            programaciones={programaciones}
            onProgramar={() => navigate('/programaciones/nueva')}
            onVerProgramaciones={() => navigate('/programaciones')}
            onVerUltima={(corrida) => navigate(`/corridas/${corrida.id}`)}
          />

          <Box display="flex" flexDirection="column" gap="1">
            <Title as="h3">Historial</Title>
            <Text fontSize="caption" color="neutral-textLow">
              Todas las actualizaciones de esta tienda, de la más nueva a la más vieja.
            </Text>
          </Box>

          {/*
            *** Tarjetas, no una tabla, y también en escritorio. ***

            Eran siete columnas más dos botones: el "Deshacer" de la última columna se
            salía del recuadro y quedaba cortado contra el borde. Se podía pelear con los
            anchos, pero el problema de fondo es otro: una corrida no es una fila de datos
            homogéneos, es un objeto con estado, fechas y acciones propias. En tarjeta la
            regla entra entera sin cortarse, cada número viaja con su etiqueta y los
            botones tienen lugar.
          */}
          <Box display="flex" flexDirection="column" gap={{ xs: '2', md: '3' }}>
            {estado.corridas.map((corrida) => (
              <TarjetaCorrida
                key={corrida.id}
                corrida={corrida}
                descripcion={descripcion(corrida)}
                deshaciendo={deshaciendo}
                onVer={() => navigate(`/corridas/${corrida.id}`)}
                /*
                  Repetir abre el wizard con la regla cargada, **nunca** ejecuta directo:
                  los precios de hoy no son los de aquella vez, así que la vista previa se
                  vuelve a mirar sí o sí. Sin `spec` no hay nada que copiar.
                */
                onRepetir={
                  corrida.regla?.spec == null
                    ? null
                    : () => navigate(`/nueva?repetir=${corrida.id}`)
                }
                onDeshacer={() => setADeshacer(corrida)}
              />
            ))}
          </Box>
        </>
      )}

      {estado.fase === 'listo' && estado.corridas.length > 0 && sesion !== null && (
        <Box>
          <Text fontSize="caption" color="neutral-textLow">
            Podés deshacer una actualización durante {sesion.plan.diasDeRetencionSnapshot} días.
            Después de esa fecha ya no guardamos los precios anteriores.
          </Text>
        </Box>
      )}

      <ModalDeshacer
        corrida={aDeshacer}
        deshaciendo={deshaciendo !== null}
        onCancelar={() => setADeshacer(null)}
        onConfirmar={() => {
          if (aDeshacer !== null) void deshacer(aDeshacer);
        }}
      />
    </Layout>
  );
}

/**
 * En qué estado está la tienda, arriba de todo.
 *
 * *** La pantalla arrancaba directamente en un registro histórico. *** Un comerciante que
 * entra no viene a auditar: viene a saber si el aumento salió y si hay otro en camino. Esas
 * dos respuestas estaban repartidas entre la primera fila de una tabla y otra pantalla.
 */
function EstadoDeLaTienda({
  ultima,
  descripcion,
  programaciones,
  onProgramar,
  onVerProgramaciones,
  onVerUltima,
}: {
  readonly ultima: Corrida | null;
  readonly descripcion: (corrida: Corrida) => string | null;
  readonly programaciones: readonly Programacion[] | null;
  readonly onProgramar: () => void;
  readonly onVerProgramaciones: () => void;
  readonly onVerUltima: (corrida: Corrida) => void;
}): ReactElement {
  const proxima = proximaProgramada(programaciones);

  return (
    <Card>
      <Card.Body>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={{ xs: '4', md: '6' }}
          // Sin `stretch` la línea vertical del medio mide cero de alto y no se ve.
          alignItems="stretch"
        >
          <Box display="flex" flexDirection="column" gap="1" flex="1" alignItems="flex-start">
            <Text fontSize="caption" color="neutral-textLow">
              Última actualización
            </Text>
            {ultima === null ? (
              <Text>Todavía no corriste ninguna.</Text>
            ) : (
              <>
                <Text fontSize="highlight" fontWeight="medium">
                  {fechaHoraRelativa(ultima.creadaEn)} · {numero(ultima.totales.actualizadas)}{' '}
                  precios
                </Text>
                {/*
                  Sin la regla esta mitad era una fecha y un número, contra tres líneas y un
                  botón del otro lado. Y una corrida de deshacer no tiene regla propia: en vez
                  de dejar el hueco, se dice qué fue.
                */}
                <Text fontSize="caption" color="neutral-textLow">
                  {descripcion(ultima) ??
                    (ultima.origen === 'undo'
                      ? 'Deshiciste una actualización anterior.'
                      : 'Sin regla guardada.')}
                </Text>
                <Box paddingTop="2">
                  <Link as="button" appearance="primary" onClick={() => onVerUltima(ultima)}>
                    Ver detalle
                  </Link>
                </Box>
              </>
            )}
          </Box>

          {/*
            En una sola columna la línea vertical no separa nada: separa el hueco.

            Y el contenedor va en `flex`, no en `block`: el divisor vertical de Nimbus es
            un `<hr>` con `height: 100%`, y un porcentaje contra un padre de alto
            automático da cero. Como ítem de un flex con `stretch` mide lo que mide la
            fila.
          */}
          <Box display={{ xs: 'none', md: 'flex' }}>
            <Divider orientation="vertical" />
          </Box>

          <Box display="flex" flexDirection="column" gap="1" flex="1" alignItems="flex-start">
            <Text fontSize="caption" color="neutral-textLow">
              Próxima programada
            </Text>
            {programaciones === null ? (
              <Text color="neutral-textLow">—</Text>
            ) : proxima === null ? (
              <>
                <Text>No tenés ninguna.</Text>
                <Text fontSize="caption" color="neutral-textLow">
                  Un aumento programado corre solo el día y la hora que elijas, y se deshace
                  igual que uno manual.
                </Text>
                <Box paddingTop="2">
                  <Button onClick={onProgramar}>Programar una actualización</Button>
                </Box>
              </>
            ) : (
              <>
                <Text fontSize="highlight" fontWeight="medium">
                  {proxima.proximaEjecucionTexto}
                </Text>
                <Text fontSize="caption" color="neutral-textLow">
                  {proxima.reglaNombre} · {proxima.descripcion}
                </Text>
                <Box paddingTop="2">
                  <Link as="button" appearance="primary" onClick={onVerProgramaciones}>
                    Ver programaciones
                  </Link>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Card.Body>
    </Card>
  );
}

/**
 * La corrida más reciente que efectivamente corrió.
 *
 * Una `preview` no tocó ningún precio: si apareciera acá, la tarjeta diría "última
 * actualización" sobre algo que el comerciante nunca confirmó.
 */
function ultimaQueCorrio(corridas: readonly Corrida[]): Corrida | null {
  return corridas.find((corrida) => corrida.estado !== 'preview') ?? null;
}

/** La activa que va a correr antes. `null` si no hay ninguna activa. */
function proximaProgramada(
  programaciones: readonly Programacion[] | null,
): Programacion | null {
  if (programaciones === null) return null;
  const activas = programaciones.filter((p) => p.activa);
  if (activas.length === 0) return null;
  return activas.reduce((mejor, actual) =>
    actual.proximaEjecucion < mejor.proximaEjecucion ? actual : mejor,
  );
}

/**
 * Lo primero que ve un comerciante recién instalado. Y el revisor de homologación.
 *
 * *** Es la pantalla que nunca habíamos mirado. *** Antes decía "Todavía no hiciste
 * ninguna actualización" y un párrafo sobre el historial que todavía no existe: le
 * hablaba de una pantalla vacía a alguien que no sabe qué hace la app.
 *
 * Tres cosas y nada más: qué hace, cómo funciona, y un solo botón. Los tres pasos están
 * porque el miedo del comerciante es que le toquemos los precios sin que él vea qué va a
 * pasar, y la respuesta a eso es el paso 2.
 */
function PrimeraVez({
  bloqueada,
  dias,
  onEmpezar,
}: {
  readonly bloqueada: boolean;
  /** Días de retención del snapshot. `null` si la sesión todavía no cargó. */
  readonly dias: number | null;
  readonly onEmpezar: () => void;
}): ReactElement {
  return (
    <Card>
      <Card.Header title="Actualizá los precios de todo tu catálogo de una vez" />
      <Card.Body>
        <Box display="flex" flexDirection="column" gap="4" alignItems="flex-start">
          <Text>
            Elegís qué productos y cuánto cambia, y actualizamos todas sus variantes. Sin
            planilla, sin tocar producto por producto.
          </Text>

          <Box display="flex" flexDirection="column" gap="2">
            <Paso numero="1" texto="Elegís qué productos: por categoría, por marca o por rango de precios." />
            <Paso
              numero="2"
              texto="Te mostramos exactamente qué precio va a quedar en cada variante. Nada se toca hasta que lo confirmes."
            />
            <Paso
              numero="3"
              texto={
                dias === null
                  ? 'Si algo no era lo que esperabas, lo deshacés y los precios vuelven como estaban.'
                  : `Si algo no era lo que esperabas, lo deshacés y los precios vuelven como estaban. Tenés ${String(dias)} días para hacerlo.`
              }
            />
          </Box>

          <Box className="acciones-a-lo-ancho" display="flex">
            <Button size="large" appearance="primary" disabled={bloqueada} onClick={onEmpezar}>
              Crear mi primera actualización
            </Button>
          </Box>
        </Box>
      </Card.Body>
    </Card>
  );
}

function Paso({
  numero,
  texto,
}: {
  readonly numero: string;
  readonly texto: string;
}): ReactElement {
  return (
    <Box display="flex" gap="2" alignItems="flex-start">
      <Text fontWeight="bold" color="primary-textLow">
        {numero}.
      </Text>
      <Text>{texto}</Text>
    </Box>
  );
}

/**
 * Si la corrida se puede deshacer **ahora**.
 *
 * El criterio vive en `estadoDeshacer`, que es el espejo del backend. Acá no se agrega
 * ninguna condición: si el botón apareciera donde el backend no deja, el comerciante
 * apretaría y recibiría un 409.
 */
function sePuedeDeshacer(corrida: Corrida): boolean {
  return estadoDeshacer(corrida).clase === 'disponible';
}

/**
 * Una corrida del historial.
 *
 * Es la misma tarjeta en 375px y en escritorio: lo único que cambia con el ancho es cómo
 * se acomodan los datos y los botones, con los sprinkles de Nimbus. Un solo árbol quiere
 * decir que lo que se prueba en el celular es lo mismo que se ve en la pantalla grande, y
 * que un cambio de copy no puede quedar aplicado en una versión y no en la otra.
 *
 * *** Dos renglones y nada más. *** La primera versión en tarjeta gastaba 140px en mostrar
 * tres números: con cinco corridas ya había que scrollear. Ganó aire y perdió densidad, que
 * en una pantalla que es un registro es peor negocio. Ahora arriba va lo que identifica la
 * corrida —cuándo, qué regla, en qué terminó— y abajo, en un solo renglón corrido, lo que
 * pasó. Entra el doble.
 *
 * *** Y las de deshacer se ven distinto. *** No son eventos equivalentes a los demás: son
 * consecuencia de otra corrida. Con fondo tenue y sangría el historial se lee como una
 * secuencia y no como una lista plana, sin necesidad de explicarlo con palabras.
 */
function TarjetaCorrida({
  corrida,
  descripcion,
  deshaciendo,
  onVer,
  onRepetir,
  onDeshacer,
}: {
  readonly corrida: Corrida;
  readonly descripcion: string | null;
  readonly deshaciendo: string | null;
  readonly onVer: () => void;
  /** `null` cuando la regla no se puede copiar: sin regla, o ilegible con el esquema de hoy. */
  readonly onRepetir: (() => void) | null;
  readonly onDeshacer: () => void;
}): ReactElement {
  const plazo = estadoDeshacer(corrida);
  const esDeshacer = corrida.origen === 'undo';

  return (
    <Box paddingLeft={{ xs: 'none', md: esDeshacer ? '6' : 'none' }}>
      <Card backgroundColor={esDeshacer ? 'neutral-surface' : 'neutral-background'}>
        <Card.Body>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            gap={{ xs: '3', md: '4' }}
          >
            <Box display="flex" flexDirection="column" gap="1" minWidth="0" flex="1">
              <Box display="flex" gap="2" alignItems="center" flexWrap="wrap">
                <Text fontWeight="medium">{fechaHoraRelativa(corrida.creadaEn)}</Text>
                <EtiquetaEstado estado={corrida.estado} />
                {descripcion !== null && (
                  <Text fontSize="caption" color="neutral-textLow">
                    {descripcion}
                  </Text>
                )}
              </Box>

              <Text fontSize="caption" color="neutral-textLow">
                {[
                  ORIGENES[corrida.origen],
                  `${numero(corrida.totales.actualizadas)} variantes actualizadas`,
                  `${numero(corrida.totales.salteadas)} sin cambios`,
                  plazo.frase,
                ]
                  .filter((parte) => parte !== null)
                  .join(' · ')}
              </Text>
            </Box>

            {/*
              "Deshacer" nunca queda pegado al botón de al lado: abajo con un renglón en el
              medio, al costado con 16px. Es la única acción de esta pantalla que reescribe
              precios en la tienda.
            */}
            {/*
              `flexShrink: 0` no es decoración: sin esto, una regla de nombre largo le come
              el ancho a la botonera y "Ver detalle" se parte en dos renglones. El texto es
              el que cede, no los botones.
            */}
            <Box
              className="acciones-a-lo-ancho"
              display="flex"
              flexDirection={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'stretch', md: 'center' }}
              flexShrink="0"
              gap="2"
            >
              {onRepetir !== null && <Button onClick={onRepetir}>Repetir</Button>}
              <Button onClick={onVer}>Ver detalle</Button>
              {sePuedeDeshacer(corrida) && (
                <Box
                  className="acciones-a-lo-ancho"
                  display="flex"
                  paddingTop={{ xs: '2', md: 'none' }}
                  paddingLeft={{ xs: 'none', md: '4' }}
                >
                  <Button
                    size="large"
                    appearance="danger"
                    disabled={deshaciendo !== null}
                    onClick={onDeshacer}
                  >
                    {deshaciendo === corrida.id ? 'Deshaciendo…' : 'Deshacer'}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Card.Body>
      </Card>
    </Box>
  );
}

const ORIGENES: Readonly<Record<Corrida['origen'], string>> = {
  manual: 'Manual',
  scheduled: 'Programada',
  undo: 'Deshacer',
};
